/**
 * The Record screen. Pure: given the record the API returned and callbacks for the actions, it
 * renders. Whether the viewer is the owner or the manager is already decided in the data: a
 * manager's record arrives without candidates, excerpts, actions or the owner's accountabilities,
 * and this component never adds any back. The manager gets one thing of their own: Propose.
 *
 * The page is the date, what is due, what closed, the three containers of work, and then what the
 * owner is accountable for beside their own lines. There is no rail: the settings that used to live
 * in one are panels in the page, because the rail was hidden at phone width and took them with it.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { Fragment, type ComponentChildren } from "preact";
import type { ClinTag, RecordAction, RecordLine, RecordUnattendedLine, RecordView, Settlement } from "@trellis/core";
import { TEAMS_CADENCE, TEAMS_NON_CADENCE, dueWeek, fmtDate, fmtDay, isCadenceItem, outcomeSegments, plural } from "@trellis/core";
import { VipEditor, type VipLine } from "./vips.js";
import { TopicEditor, type TopicLine } from "./topics.js";
import { Picker } from "../components/picker.js";
import { Tip } from "../components/tip.js";
import { DueColumns, OutcomeBar } from "../components/charts.js";
import { Link } from "../components/link.js";

export type ActionBody = { dueDate?: string; reason?: string; text?: string; title?: string; to?: string };
export type OnAction = (commitmentId: string, action: RecordAction, body?: ActionBody) => Promise<void>;
export interface Person { upn: string; name: string }
export type InitiativesOf = (programId: string) => Promise<{ id: string; name: string }[]>;
export type OnInitiative = (commitmentId: string, initiativeId: string | null, milestone: boolean) => Promise<void>;
export type PeopleOf = (programId: string) => Promise<Person[]>;
/** One answer to one resemblance: this line repeats `of`, or the two are different things. */
export type OnResemblance = (commitmentId: string, of: string, verdict: Settlement) => Promise<void>;

export interface RecordScreenProps {
  record: RecordView;
  onAction?: OnAction | undefined;
  /** The owner adds a commitment of their own. */
  onAdd?: ((programId: string, title: string, dueDate: string) => Promise<void>) | undefined;
  /** The manager proposes one to the person whose record this is. */
  onPropose?: ((programId: string, title: string, dueDate: string) => Promise<void>) | undefined;
  /** Who is on a program, for the transfer and delegate pickers. */
  peopleOf?: PeopleOf | undefined;
  /** Initiatives the owner may put this line on, by program. Absent: the control is not offered. */
  initiativesOf?: InitiativesOf | undefined;
  onInitiative?: OnInitiative | undefined;
  /** Settling a resemblance one of the owner's lines carries. Owner only. */
  onResemblance?: OnResemblance | undefined;
  /** The owner's time zone (null: the default) and how to change it. Owner only. */
  timezone?: string | null | undefined;
  onTimezone?: ((tz: string | null) => Promise<void>) | undefined;
  /** A message from the last action, shown under the headline. */
  notice?: { text: string; error?: boolean } | undefined;
  /** Commitment id from `/app/i/:id`, `/app?c=` or `/app/people/:upn?c=`; that line opens on its own. */
  focusId?: string | undefined;
  /** Owner only: their personal VIP layer and inherited layers they cannot edit. */
  vips?: { personal: VipLine[]; inherited: (VipLine & { relation: string })[] } | undefined;
  onVips?: ((personal: VipLine[]) => Promise<void>) | undefined;
  topics?: TopicLine[] | undefined;
  onTopics?: ((topics: TopicLine[]) => Promise<void>) | undefined;
}

export const DEFAULT_TIMEZONE = "America/New_York";

/** A note from somebody other than the owner says who, and what they were. */
const ROLE_NAME: Record<string, string> = {
  "delivery-lead": "delivery lead",
  "deputy-delivery-lead": "deputy delivery lead",
  "program-admin": "program admin",
  "portfolio-lead": "portfolio lead",
  "technical-lead": "technical lead",
};

/** Fragments on one line, middle dots between them, and a sentence when there are none. */
function joinDots(parts: ComponentChildren[], empty: string): ComponentChildren {
  if (parts.length === 0) return empty;
  return parts.map((part, i) => <Fragment key={i}>{i > 0 ? " · " : ""}{part}</Fragment>);
}

/** "Thursday 24 September": the headline is the day, because the page is about today. */
function longDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(d);
}

export function RecordScreen({ record, onAction, onAdd, onPropose, peopleOf, initiativesOf, onInitiative, onResemblance, timezone, onTimezone, notice, vips, onVips, topics, onTopics, focusId }: RecordScreenProps) {
  const mine = record.viewerIsOwner;
  const name = record.ownerName || record.owner;
  const first = name.split(/\s+/)[0] ?? name;
  const [form, setForm] = useState<"add" | "propose" | undefined>(undefined);
  // Candidates are their own set, not part of `lines`, and the ranked working set is capped at
  // twenty: a candidate past the cap is on the Candidates list and has to open from there too.
  const focused = focusId ? [...record.working, ...record.cadences, ...record.lines, ...(record.candidates ?? [])].find((l) => l.id === focusId) : undefined;
  const restWorking = focused ? record.working.filter((l) => l.id !== focused.id) : record.working;
  const restCadences = focused ? record.cadences.filter((l) => l.id !== focused.id) : record.cadences;
  // `working` is the ranked live set and holds both kinds; the two boxes are the two
  // halves of it, and each heading counts everything of that kind, not just what fits.
  const openNow = restWorking.filter((l) => l.status === "open");
  const candidatesNow = restWorking.filter((l) => l.status === "candidate");
  // Cadences are counted in their own box, so a count of "open" here has to leave them
  // out or the two boxes both claim the same rows.
  //
  // Every count below is of the whole set, never of what the box happens to be rendering.
  // A row the digest linked to is lifted out of its box into the card at the top, and it
  // is still on the record: subtracting it made the heading, the summary and the bar's
  // badge disagree by one for as long as `/app/i/:id` was open.
  const openTotal = record.lines.filter((l) => l.status === "open" && !isCadenceItem(l)).length;
  const candidateTotal = (record.candidates ?? []).length;
  // The same set the cadence box counts and the bar's badge counts: a cadence is on this
  // list because Trellis opened it and is waiting for an answer, which is what "due" means.
  const cadencesDue = record.cadences.length;
  const who = mine ? "" : `?upn=${encodeURIComponent(record.owner)}`;
  const acts = { mine, owner: record.owner, onAction, peopleOf, initiativesOf, onInitiative, onResemblance };
  // The summary names what is actually waiting. A zero says nothing worth the space, so a
  // fragment only appears when it has a count, and a page with none says so in words.
  const waiting: ComponentChildren[] = [
    ...(mine && candidateTotal ? [<Link class="link" href="/app/candidates">{plural(candidateTotal, "candidate")}</Link>] : []),
    ...(openTotal ? [<Link class="link" href={`/app/commitments${who}`}>{openTotal} open</Link>] : []),
    ...(cadencesDue ? [<Link class="link" href={`/app/cadences${who}`}>{plural(cadencesDue, "cadence")} due</Link>] : []),
    ...(record.counts.asksUnacknowledgedPastStandard
      ? [<span class="tone-bad">{plural(record.counts.asksUnacknowledgedPastStandard, "ask")} past {record.ackStandard}</span>]
      : []),
  ];
  return (
    <main class="main wide record">
      <div class="record-head">
        <h1 class="h-xl">{mine ? longDate(record.asOf) : name}</h1>
        <p class="note record-sum">
          {joinDots(waiting, mine ? "Nothing is waiting on you." : "Nothing open.")}
          {!mine && <>{" · "}{longDate(record.asOf)}</>}
        </p>
      </div>
      {record.roles.length > 0 && <p class="note dim record-roles">{describeRoles(record)}</p>}
      {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}

      {/* One line, opened on its own. It sits above the record rather than inside it so that
          arriving at `/app/i/:id` puts the thing you asked for first and scrolls nothing. */}
      {focused && (
        <section class="box focus-box">
          <div class="box-head">
            <h2 class="box-title">{isCadenceItem(focused) ? "This cadence" : focused.status === "candidate" ? "This candidate" : "This commitment"}</h2>
            <Link class="box-all" href={mine ? "/app" : `/app/people/${encodeURIComponent(record.owner)}`}>{mine ? "Back to your record" : `Back to ${first}’s record`}</Link>
          </div>
          <div class="box-body"><Item line={focused} {...acts} focus /></div>
        </section>
      )}

      <div class="charts">
        <DueColumns days={dueWeek(record)} />
        <OutcomeBar days={record.days} segments={outcomeSegments(record)} />
      </div>

      {!mine && onPropose && (
        <div class="record-tools">
          {!form && <button class="btn small" onClick={() => setForm("propose")}>Propose to {first}…</button>}
          {form === "propose" && <NewCommitment programs={record.programs} verb={`Propose to ${first}`} onSubmit={onPropose} onDone={() => setForm(undefined)} />}
        </div>
      )}

      <div class="board">
        <div class="board-main">
          {mine && (
            <Box
              title="Candidates" noun="candidate" href="/app/candidates" count={candidateTotal} shown={candidatesNow.length}
              why="Proposed to you. Nothing lands until you confirm."
              what="Things Trellis heard you might have taken on. Nothing reaches your record until you confirm it, and nobody else sees the source."
              empty="Nothing waiting on you."
            >
              {candidatesNow.map((l) => <Item key={l.id} line={l} {...acts} />)}
            </Box>
          )}
          <Box
            title="Commitments" noun="commitment" href={`/app/commitments${who}`} count={openTotal} shown={openNow.length}
            why={record.counts.closed ? `Confirmed. ${record.counts.closed} closed in the last ${plural(record.days, "day")}.` : "Confirmed work with a date you accepted."}
            what="Work you have accepted that is still open, most pressing first."
            empty="Nothing open. Anything you accept lands here."
            extra={mine && onAdd
              ? (form === "add"
                  ? <NewCommitment programs={record.programs} verb="Add" onSubmit={onAdd} onDone={() => setForm(undefined)} />
                  : <button class="btn small quiet" onClick={() => setForm("add")}>Add a commitment</button>)
              : undefined}
          >
            {openNow.map((l) => <Item key={l.id} line={l} {...acts} />)}
          </Box>
        </div>
        <Box
          title="Cadences" noun="cadence" href={`/app/cadences${who}`} count={cadencesDue} shown={restCadences.length} tall
          why="Recurring. Trellis opens each one and closes it when you answer."
          what="The recurring reports and meetings the delivery model says you run. They appear on their own schedule; closing one records that it happened."
          empty="No cadence falls to you this period."
        >
          {restCadences.map((l) => <Item key={l.id} line={l} owner={record.owner} mine={mine} onAction={onAction} peopleOf={peopleOf} onResemblance={onResemblance} />)}
        </Box>
      </div>

      {mine && (
        <div class="deck">
          <UnitsPanel record={record} />
          <ReportsPanel record={record} />
          <UnattendedPanel record={record} />
          <ListsPanel vips={vips} onVips={onVips} topics={topics} onTopics={onTopics} />
          {timezone !== undefined && <CardsPanel timezone={timezone} onTimezone={onTimezone} />}
        </div>
      )}

      <p class="record-foot">
        <Lock />
        <span>{mine ? "Visible to you, your manager and the leads on your programs." : `Visible to ${first}, to you, and to the leads on their programs.`}</span>
      </p>
    </main>
  );
}

/**
 * A container with a heading that is itself the way into the full list. The count sits
 * beside the heading and is the same link, because a reader aims at the number.
 */
function Box({ title, noun, href, count, shown, what, why, empty, tall, extra, children }: {
  title: string; noun: string; href: string; count: number; shown: number; what: string; why: string; empty: string;
  tall?: boolean; extra?: ComponentChildren; children?: ComponentChildren;
}) {
  return (
    <section class={`box${tall ? " tall" : ""}`}>
      <div class="box-head">
        <h2 class="box-title"><Tip what={what}>{title}</Tip></h2>
        <Link class="box-count num" href={href}>{count}</Link>
      </div>
      <p class="box-why">{why}</p>
      {/* "Nothing here" is only true when there is nothing. A box whose rows are all held
          back, or whose only row was lifted into the digest card, says that instead. */}
      {shown === 0
        ? (count === 0 ? <p class="box-empty">{empty}</p> : null)
        : <div class="box-body">{children}</div>}
      {count > shown && <p class="box-held">{count - shown}{shown ? " more" : ""} not shown here.</p>}
      <div class="box-foot">
        {count > 0 && <Link class="box-all" href={href}>All {plural(count, noun)}</Link>}
        {extra}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ the panels under the board

/** A tile in the deck: one heading, one explanation, one list. */
function Tile({ title, what, wide, children, foot }: { title: string; what: string; wide?: boolean; children: ComponentChildren; foot?: ComponentChildren }) {
  return (
    <section class={`tile deck-tile${wide ? " span" : ""}`}>
      <h3 class="tile-title"><Tip what={what}>{title}</Tip></h3>
      {children}
      {foot && <p class="tile-foot">{foot}</p>}
    </section>
  );
}

/** The big right-hand number on a unit or a person, with the word under it. */
function Count({ n, word }: { n: number; word: string }) {
  return <span class="n num">{n}<small>{word}</small></span>;
}

function UnitsPanel({ record }: { record: RecordView }) {
  const units = record.side?.units ?? [];
  return (
    <Tile title="Your units" what="Teams and workstreams where you are the named lead. The number is confirmed open work on that unit and everything under it.">
      {units.length === 0
        ? <p class="tile-empty">You are not the named lead of a unit.</p>
        : (
          <div class="units">
            {units.map((u) => (
              <Link key={u.unitId} class={`unit${u.clin ? ` clin-${u.clin.tone}` : ""}`} href={`/app/programs/${encodeURIComponent(u.programId)}/units/${encodeURIComponent(u.unitId)}`}>
                <b>{u.unitName}</b>
                <Count n={u.openWork} word="open" />
                <span class="sub">
                  {u.kind === "team" ? "Team" : "Workstream"} · {u.clin ? `CLIN ${u.clin.key}` : "No CLIN"} · {u.programName ?? u.programId} · {u.people ? plural(u.people, "person", "people") : "nobody on it"}
                  {u.unattendedCount > 0 && <span class="watch-text"> · {u.unattendedCount} unanswered</span>}
                </span>
              </Link>
            ))}
          </div>
        )}
    </Tile>
  );
}

function ReportsPanel({ record }: { record: RecordView }) {
  const reports = record.side?.reports ?? [];
  return (
    <Tile
      title="Your reports"
      what="People whose manager you are, from the directory. The number is their confirmed open work on the contracts you are on; work of theirs on a contract you are not on is not counted."
      foot="Subcontractors have no directory manager, so they never appear here."
    >
      {reports.length === 0
        ? <p class="tile-empty">Nobody reports to you.</p>
        : (
          <div class="units">
            {reports.map((r) => (
              <Link key={r.upn} class="unit" href={`/app/people/${encodeURIComponent(r.upn)}`}>
                <b>{r.name}</b>
                <Count n={r.openWork} word="open" />
                <span class="sub">
                  {r.units.length ? r.units.join(" · ") : "On no unit"}
                  {r.unattendedCount > 0 && <span class="watch-text"> · {r.unattendedCount} unanswered</span>}
                </span>
              </Link>
            ))}
          </div>
        )}
    </Tile>
  );
}

function UnattendedPanel({ record }: { record: RecordView }) {
  const waiting = record.side?.unattended ?? [];
  const leadsNothing = (record.side?.units ?? []).length === 0;
  const reports = new Set((record.side?.reports ?? []).map((r) => r.upn));
  return (
    <Tile
      title="Unattended on your units"
      what="Work on units you lead that nobody has confirmed, declined or moved for longer than the programme allows. Nothing was re-sent and it stays on its owner's record; you are shown it because you are accountable for the unit."
      wide
      foot={waiting.length ? "Nothing has been re-sent, and what it came from stays with its owner." : undefined}
    >
      {waiting.length === 0
        ? <p class="tile-empty">{leadsNothing ? "Nothing here until you lead a unit." : "Nothing on your units is waiting for an answer."}</p>
        : <div class="drifts">{waiting.map((w) => <Drift key={w.id} item={w} linkOwner={reports.has(w.ownerUpn)} />)}</div>}
    </Tile>
  );
}

function Drift({ item, linkOwner }: { item: RecordUnattendedLine; linkOwner: boolean }) {
  return (
    <div class="drift">
      <b>{item.title}</b>
      <p class="sub">
        <span class="age">{item.days ? plural(item.days, "day") : "under a day"}</span>{" · "}
        {linkOwner ? <Link class="link" href={`/app/people/${encodeURIComponent(item.ownerUpn)}`}>{item.owner}</Link> : item.owner}
        {" · "}{item.unitName}{item.viaOwner ? " (where they sit; the item is on no unit yet)" : ""}
      </p>
    </div>
  );
}

/** VIPs and topics as a summary, with the editors behind a disclosure rather than always open. */
function ListsPanel({ vips, onVips, topics, onTopics }: {
  vips: RecordScreenProps["vips"]; onVips: RecordScreenProps["onVips"];
  topics: RecordScreenProps["topics"]; onTopics: RecordScreenProps["onTopics"];
}) {
  if (!vips && !topics) return null;
  const all = vips ? [...vips.personal, ...vips.inherited] : [];
  return (
    <Tile
      title="VIPs and topics"
      what="A VIP is a person whose mail always surfaces. A topic does the same for a phrase, whoever sent it. Your manager and theirs are VIPs automatically."
      foot="How often each matched is not counted yet."
    >
      <div class="units">
        <span class="unit unit-flat">
          <b>VIPs</b>
          <Count n={all.length} word="in all" />
          <span class="sub">
            {all.length ? all.slice(0, 3).map((v) => v.name).join(", ") + (all.length > 3 ? `, +${all.length - 3}` : "") : "Nobody yet"}
            {vips?.personal.length ? ` · ${vips.personal.length} yours` : ""}
          </span>
        </span>
        <span class="unit unit-flat">
          <b>Topics</b>
          <Count n={topics?.length ?? 0} word="yours" />
          <span class="sub">{topics?.length ? `${topics.map((t) => t.name).join(", ")} · ${plural((topics ?? []).reduce((n, t) => n + t.match.length, 0), "phrase")}` : "Nothing yet"}</span>
        </span>
      </div>
      {vips && onVips && (
        <details class="fold">
          <summary><span class="label fold-title">Edit your VIPs</span></summary>
          {vips.inherited.length > 0 && (
            <div class="inherited">
              {vips.inherited.map((v) => <div class="tz-line" key={`${v.relation}:${v.address}`}>{v.name} · {v.relation}{v.role ? ` · ${v.role}` : ""}</div>)}
            </div>
          )}
          <VipEditor entries={vips.personal} kindDefault="internal" onSave={onVips} />
        </details>
      )}
      {topics && onTopics && (
        <details class="fold">
          <summary><span class="label fold-title">Edit your topics</span></summary>
          <p class="s">Yours alone. A VIP says whose mail is always read; a topic says what is always read, whoever sent it.</p>
          <TopicEditor entries={topics} onSave={onTopics} scope="person" />
        </details>
      )}
    </Tile>
  );
}

/** When the cards arrive. The times are fixed for everyone; the zone they are read in is not. */
function CardsPanel({ timezone, onTimezone }: { timezone: string | null; onTimezone: ((tz: string | null) => Promise<void>) | undefined }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(timezone ?? DEFAULT_TIMEZONE);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!onTimezone) return;
    setBusy(true);
    try { await onTimezone(value === DEFAULT_TIMEZONE ? null : value); setEditing(false); } finally { setBusy(false); }
  };
  return (
    <Tile
      title="When Trellis messages you"
      what="The cards Trellis sends in Teams: when they go out, in which zone, and how much goes in one."
      foot="A card is capped so it opens quickly on a phone. The times are the same for everybody; only the zone is yours."
    >
      <dl class="facts">
        <div><dt>Cards</dt><dd class="num">06:00 · 12:00 · 16:00</dd></div>
        <div><dt>Zone</dt><dd>{timezone ?? DEFAULT_TIMEZONE}</dd></div>
        <div><dt>Most in one card</dt><dd class="num">{TEAMS_NON_CADENCE} items and {TEAMS_CADENCE} cadences</dd></div>
      </dl>
      {onTimezone && (editing
        ? <div class="inline-form">
            <Picker label="Time zone" options={timezones().map((z) => ({ value: z, label: z }))} value={value} onChange={setValue} placeholder="Search zones…" />
            <div class="buttons"><button class="btn small" disabled={busy} onClick={() => void save()}>Save</button><button class="link" onClick={() => setEditing(false)}>Cancel</button></div>
          </div>
        : <button class="btn small" onClick={() => setEditing(true)}>Change the zone</button>)}
    </Tile>
  );
}

const FALLBACK_ZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "UTC", "Europe/London", "Europe/Berlin", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Australia/Sydney"];
function timezones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try { const all = intl.supportedValuesOf?.("timeZone"); if (all && all.length) return all; } catch { /* older runtime */ }
  return FALLBACK_ZONES;
}

/** Title, date, and the program when there is a choice. Used to add one's own and to propose to a report. */
function NewCommitment({ programs, verb, onSubmit, onDone }: { programs: { id: string; name: string }[]; verb: string; onSubmit: (programId: string, title: string, dueDate: string) => Promise<void>; onDone: () => void }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [programId, setProgramId] = useState(programs[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const ok = title.trim().length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(date) && !!programId;
  const submit = async () => { setBusy(true); try { await onSubmit(programId, title.trim(), date); onDone(); } finally { setBusy(false); } };
  if (programs.length === 0) return <div class="notice error">No program to add to.</div>;
  return (
    <div class="inline-form new-commitment">
      <label class="sr-only" for="new-title">What, in one line</label>
      <input id="new-title" type="text" value={title} placeholder="What, in one line" onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
      <div class="fields">
        <label class="sr-only" for="new-due">The date it is due</label>
        <input id="new-due" type="date" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} />
        {programs.length > 1
          ? <Picker label="Program" options={programs.map((p) => ({ value: p.id, label: p.name }))} value={programId} onChange={setProgramId} placeholder="Search programs…" />
          : <span class="muted">{programs[0]!.name}</span>}
      </div>
      <div class="buttons"><button class="btn" disabled={busy || !ok} onClick={() => void submit()}>{verb}</button><button class="link" onClick={onDone}>Cancel</button></div>
    </div>
  );
}

// ------------------------------------------------------------------ a row, and what may be done to it

export type RowForm = "note" | "close" | "move" | "decline" | "transfer" | "delegate" | "reassign" | "retitle" | "initiative";

export interface RowProps {
  line: RecordLine;
  mine: boolean;
  onAction?: OnAction | undefined;
  peopleOf?: PeopleOf | undefined;
  initiativesOf?: InitiativesOf | undefined;
  onInitiative?: OnInitiative | undefined;
  /** Settling a resemblance. Absent: the line states one and offers no answer to it. */
  onResemblance?: OnResemblance | undefined;
}

const LABEL: Record<RecordAction, string> = {
  confirm: "Confirm", "set-aside": "Set aside", restore: "Restore", close: "Close", move: "Move date",
  note: "Add a note", accept: "Accept", decline: "Decline", "accept-transfer": "Accept", "decline-transfer": "Decline",
  transfer: "Transfer…", delegate: "Delegate…", assign: "Assign…", reassign: "Reassign…", retitle: "Reword",
};

/** The actions that open a form rather than running straight away. */
const ASKS_FIRST: Partial<Record<RecordAction, RowForm>> = {
  decline: "decline", close: "close", move: "move", note: "note", retitle: "retitle",
  transfer: "transfer", delegate: "delegate", reassign: "reassign", assign: "reassign",
};

/**
 * The two or three that matter, in the order somebody reaches for them. Everything else goes
 * behind More: a row that spills eight links over two lines is a toolbar, not a decision.
 */
function primaryOf(line: RecordLine): RecordAction[] {
  const has = (a: RecordAction) => line.actions.includes(a);
  if (has("accept")) return ["accept", "decline", "move"];
  if (has("accept-transfer")) return ["accept-transfer", "decline-transfer"];
  if (has("confirm")) return ["confirm", "set-aside"];
  if (has("close")) return ["close", "move"];
  if (has("restore")) return ["restore"];
  return line.actions.slice(0, 2);
}

export interface RowState {
  line: RecordLine;
  form: RowForm | undefined;
  busy: boolean;
  primary: RecordAction[];
  more: RecordAction[];
  offersInitiative: boolean;
  start: (a: RecordAction) => void;
  open: (f: RowForm) => void;
  cancel: () => void;
  run: (a: RecordAction, body?: ActionBody) => Promise<void>;
  text: string; setText: (v: string) => void;
  date: string; setDate: (v: string) => void;
  to: string; setTo: (v: string) => void;
  people: Person[] | undefined;
  inits: { id: string; name: string }[] | undefined;
  initId: string; setInitId: (v: string) => void;
  milestone: boolean; setMilestone: (v: boolean) => void;
  onInitiative: OnInitiative | undefined;
  setBusy: (v: boolean) => void;
}

/** Everything a row's controls need, so the record's card and the list's table share one behaviour. */
export function useRow({ line, mine, onAction, peopleOf, initiativesOf, onInitiative }: RowProps): RowState {
  const [form, setForm] = useState<RowForm | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [date, setDate] = useState(line.dueDate);
  const [to, setTo] = useState("");
  const [people, setPeople] = useState<Person[] | undefined>(undefined);
  const [inits, setInits] = useState<{ id: string; name: string }[] | undefined>(undefined);
  const [initId, setInitId] = useState("");
  const [milestone, setMilestone] = useState(false);
  const can = (a: RecordAction) => mine && !!onAction && line.actions.includes(a);
  const offered = line.actions.filter(can);
  const primary = primaryOf(line).filter(can);
  const offersInitiative = mine && !!initiativesOf && !!onInitiative && line.status !== "closed";
  const run = async (a: RecordAction, body?: ActionBody) => {
    if (!onAction) return;
    setBusy(true);
    try { await onAction(line.id, a, body); setForm(undefined); setText(""); setTo(""); } finally { setBusy(false); }
  };
  const open = (f: RowForm) => {
    setForm(f);
    if ((f === "transfer" || f === "delegate" || f === "reassign") && !people && peopleOf) void peopleOf(line.programId).then(setPeople, () => setPeople([]));
    if (f === "initiative" && !inits && initiativesOf) void initiativesOf(line.programId).then(setInits, () => setInits([]));
  };
  const start = (a: RecordAction) => {
    const asks = ASKS_FIRST[a];
    if (!asks) { void run(a); return; }
    if (a === "retitle") setText(line.title);
    open(asks);
  };
  useEffect(() => { if (people && !to) setTo(people[0]?.upn ?? ""); }, [people]);
  return {
    line, form, busy, primary, more: offered.filter((a) => !primary.includes(a)), offersInitiative,
    start, open, cancel: () => { setForm(undefined); setText(""); }, run,
    text, setText, date, setDate, to, setTo, people, inits, initId, setInitId, milestone, setMilestone,
    onInitiative, setBusy,
  };
}

/** The buttons: the ones that matter, then a More that reveals the rest in place. */
export function RowButtons({ state }: { state: RowState }) {
  const [open, setOpen] = useState(false);
  const moreBtn = useRef<HTMLButtonElement>(null);
  const listId = `more-${state.line.id}`;
  const extra = state.more.length + (state.offersInitiative ? 1 : 0);
  if (state.primary.length === 0 && extra === 0) return null;
  const close = () => { setOpen(false); moreBtn.current?.focus(); };
  return (
    <div class="acts" onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); close(); } }}>
      {state.primary.map((a, i) => (
        <button key={a} type="button" class={`btn small${i === 0 ? " go" : ""}`} disabled={state.busy} onClick={() => state.start(a)}>{LABEL[a]}</button>
      ))}
      {extra > 0 && (
        <button ref={moreBtn} type="button" class="btn small quiet" aria-expanded={open} aria-controls={listId} disabled={state.busy} onClick={() => setOpen(!open)}>
          More{open ? "" : ` (${extra})`}
        </button>
      )}
      <div class="acts-more" id={listId} hidden={!open}>
        {state.more.map((a) => (
          <button key={a} type="button" class="btn small" disabled={state.busy} onClick={() => { setOpen(false); state.start(a); }}>{LABEL[a]}</button>
        ))}
        {state.offersInitiative && (
          <button type="button" class="btn small" disabled={state.busy} onClick={() => { setOpen(false); state.open("initiative"); }}>On an initiative…</button>
        )}
      </div>
    </div>
  );
}

/** Whatever the chosen action needs said before it happens. Nothing when no form is open. */
export function RowForms({ state }: { state: RowState }) {
  const { line, form } = state;
  if (!form) return null;
  const isProposal = line.actions.includes("accept");
  const cancel = <button class="link" onClick={state.cancel}>Cancel</button>;
  if (form === "note" || form === "close" || form === "decline") {
    const placeholder = form === "note" ? "Your note" : form === "close" ? "A note, if you want one" : "Why, if you want to say";
    const verb = form === "note" ? "Save note" : form === "close" ? "Close it" : "Decline";
    const send = () => {
      if (form === "note") return state.run("note", { text: state.text });
      if (form === "close") return state.run("close", state.text.trim() ? { text: state.text } : undefined);
      return state.run(line.actions.includes("decline") ? "decline" : "decline-transfer", state.text.trim() ? { reason: state.text } : undefined);
    };
    return (
      <div class="inline-form">
        <label class="sr-only" for={`t-${line.id}`}>{placeholder}</label>
        <textarea id={`t-${line.id}`} value={state.text} onInput={(e) => state.setText((e.target as HTMLTextAreaElement).value)} placeholder={placeholder} />
        <div class="buttons">
          <button class="btn small go" disabled={state.busy || (form === "note" && !state.text.trim())} onClick={() => void send()}>{verb}</button>
          {cancel}
        </div>
      </div>
    );
  }
  if (form === "retitle") {
    return (
      <div class="inline-form">
        <label class="sr-only" for={`retitle-${line.id}`}>What this item says</label>
        <textarea id={`retitle-${line.id}`} value={state.text} onInput={(e) => state.setText((e.target as HTMLTextAreaElement).value)} placeholder="Say it the way you would" />
        <div class="buttons">
          <button class="btn small go" disabled={state.busy || !state.text.trim() || state.text.trim() === line.title} onClick={() => void state.run("retitle", { title: state.text.trim() })}>Save wording</button>
          {cancel}
          <span class="muted">The original words stay on the source.</span>
        </div>
      </div>
    );
  }
  if (form === "move") {
    return (
      <div class="inline-form">
        <label class="sr-only" for={`due-${line.id}`}>New date</label>
        <input id={`due-${line.id}`} type="date" value={state.date} onInput={(e) => state.setDate((e.target as HTMLInputElement).value)} />
        <label class="sr-only" for={`why-${line.id}`}>Why it moved</label>
        <textarea id={`why-${line.id}`} value={state.text} onInput={(e) => state.setText((e.target as HTMLTextAreaElement).value)} placeholder={isProposal ? "A note, if you want one" : "Why it moved"} />
        <div class="buttons">
          <button class="btn small go" disabled={state.busy || !state.date} onClick={() => void state.run("move", { dueDate: state.date, ...(state.text.trim() ? { reason: state.text } : {}) })}>Move to {state.date ? fmtDay(state.date) : "…"}</button>
          {cancel}
        </div>
      </div>
    );
  }
  if (form === "initiative") {
    const put = state.onInitiative;
    if (!put) return null;
    return (
      <div class="inline-form">
        {!state.inits ? <span class="muted">Reading initiatives…</span>
          : state.inits.length === 0 ? <span class="muted">No initiative on this program yet.</span>
          : <Picker label="Which initiative?" options={state.inits.map((i) => ({ value: i.id, label: i.name }))} value={state.initId} onChange={state.setInitId} placeholder="Search initiatives…" clearable />}
        {state.inits && state.inits.length > 0 && (
          <label class="s check">
            <input type="checkbox" checked={state.milestone} onChange={(e) => state.setMilestone((e.target as HTMLInputElement).checked)} /> This is a milestone
          </label>
        )}
        <div class="buttons">
          <button class="btn small go" disabled={state.busy || !state.initId} onClick={() => { state.setBusy(true); void put(line.id, state.initId, state.milestone).finally(() => { state.setBusy(false); state.cancel(); }); }}>Put it on</button>
          <button class="link" disabled={state.busy} onClick={() => { state.setBusy(true); void put(line.id, null, false).finally(() => { state.setBusy(false); state.cancel(); }); }}>Take it off</button>
          {cancel}
          <span class="muted">A label, like a unit. It stays yours and stays on this program.</span>
        </div>
      </div>
    );
  }
  const verb = form === "transfer" ? "Transfer" : form === "delegate" ? "Delegate" : "Reassign";
  return (
    <div class="inline-form">
      {!state.people
        ? <span class="muted">Reading the roster…</span>
        : state.people.length === 0
          ? <span class="muted">Nobody else on the program.</span>
          : <Picker label="Whose is this?" options={state.people.map((p) => ({ value: p.upn, label: p.name, hint: p.upn }))} value={state.to} onChange={state.setTo} placeholder="Search people…" />}
      <div class="buttons">
        <button class="btn small go" disabled={state.busy || !state.to} onClick={() => void state.run(form === "reassign" ? "reassign" : form, { to: state.to })}>{verb}</button>
        {cancel}
        <span class="muted">{form === "transfer" ? "They accept before it moves." : form === "delegate" ? "You stay accountable." : "Still a candidate until they accept."}</span>
      </div>
    </div>
  );
}

/**
 * A line's own address. `/app/i/:id` resolves against the reader's own record, which is what the
 * digest links to, so somebody else's line keeps its owner in the path and names the line in `?c=`.
 */
export function itemHref(id: string, owner: string, mine: boolean): string {
  return mine ? `/app/i/${encodeURIComponent(id)}` : `/app/people/${encodeURIComponent(owner)}?c=${encodeURIComponent(id)}`;
}

/**
 * What the line opened on its own says that a row in a list has no room for. The relative date a
 * row shows ("due in 3 days") is the one thing a reader cannot act on, so the date itself leads.
 */
function ItemFacts({ line }: { line: RecordLine }) {
  const facts: { k: string; v: string }[] = [
    { k: line.status === "candidate" ? "Date it states" : "Due", v: fmtDate(line.dueDate) },
    ...(line.closedAt ? [{ k: "Closed", v: fmtDate(line.closedAt) }] : []),
    ...(line.milestone ? [{ k: "Initiative", v: "A milestone" }] : []),
  ];
  return (
    <dl class="item-facts">
      {facts.map((f) => <div key={f.k}><dt>{f.k}</dt><dd>{f.v}</dd></div>)}
    </dl>
  );
}

/** The line as the record shows it: what it is, where it stands, what funds it, what you can do. */
export function Item({ line, focus, owner, ...props }: RowProps & { focus?: boolean; owner: string }) {
  const state = useRow({ line, ...props });
  return (
    <article class={`item${focus ? " focus" : ""}`} id={`c-${line.id}`}>
      <p class="item-t">
        {line.askedBy && <span class="asker">{line.askedBy}</span>}
        {focus ? line.title : <Link class="item-open" href={itemHref(line.id, owner, props.mine)}>{line.title}</Link>}
      </p>
      <p class={`item-state tone-${line.outcome.tone}`}>
        {line.outcome.label}
        {line.outcome.detail && <span class="detail">{line.outcome.detail}</span>}
        {line.originalDueDate && line.originalDueDate !== line.dueDate && <span class="detail">First accepted {fmtDay(line.originalDueDate)}, now {fmtDay(line.dueDate)}</span>}
      </p>
      <Resemblance line={line} owner={owner} mine={props.mine} onResemblance={props.onResemblance} />
      <RepeatedBy line={line} owner={owner} mine={props.mine} />
      {focus && <ItemFacts line={line} />}
      <AttentionWhy line={line} />
      <p class="item-meta">
        <ClinTagView clin={line.clin} />
        <span class="item-where">{whereLine(line)}</span>
      </p>
      <p class="item-src">
        {line.source.excerpt
          ? <details><summary>{sourceLine(line)}{sourceOpen(line)}</summary><span class="excerpt">“{line.source.excerpt}”{line.source.speaker ? ` — ${line.source.speaker}` : ""}</span></details>
          : <span>{sourceLine(line)}{sourceOpen(line)}</span>}
      </p>
      {line.notes.length > 0 && (
        <div class="item-notes">
          {line.notes.map((n, i) => (
            <p key={i}>
              {n.byRole && n.byRole !== "owner" && <b class="note-by">{n.byName ?? n.by}{ROLE_NAME[n.byRole] ? `, ${ROLE_NAME[n.byRole]}` : ""} asked: </b>}
              {n.text}
            </p>
          ))}
        </div>
      )}
      {!state.form && <RowButtons state={state} />}
      <RowForms state={state} />
    </article>
  );
}

/**
 * The resemblance on a line, and both answers to it.
 *
 * Both are offered whichever way it stands, because the premise of the whole thing is that two
 * near-identical titles can be two real obligations: somebody told "might repeat" who can only agree
 * is being led, and somebody who pressed the wrong one needs the way back. Nothing here closes,
 * hides or drops either row (PR #44); the pair is a comparison and this records how it came out.
 *
 * "Compare them" opens the other item on its own, not an anchor on this page. The anchor went nowhere
 * silently wherever the survivor was not being rendered, which is most of the time: the record draws
 * the ranked open set and the candidates, so a closed survivor, one past the cap, or anything at all
 * on `/app/i/:id` was not there to jump to.
 */
function Resemblance({ line, owner, mine, onResemblance }: { line: RecordLine; owner: string; mine: boolean; onResemblance?: OnResemblance | undefined }) {
  const [busy, setBusy] = useState(false);
  const dup = line.duplicateOf;
  if (!dup) return null;
  const named = dup.title ? <> “{dup.title}”</> : " another item";
  const says = dup.settled === "repeats"
    ? <>Marked as repeating{named} already on this record.</>
    : dup.settled === "different"
      ? <>Compared with{named} on this record and kept as its own item.</>
      : <>Might repeat{named} already on this record.</>;
  const settle = (verdict: Settlement) => {
    if (!onResemblance) return;
    setBusy(true);
    void onResemblance(line.id, dup.id, verdict).finally(() => setBusy(false));
  };
  return (
    <>
      {/* The sentence names the pair, so it is also the accessible name of the two buttons' group:
          one row's controls are told apart from another's without any text a reader cannot see. */}
      <p class="item-dup" id={`dup-${line.id}`}>
        {says}{" "}<Link href={itemHref(dup.id, owner, mine)}>Compare them</Link>
      </p>
      {mine && onResemblance && (
        <div class="dup-acts" role="group" aria-labelledby={`dup-${line.id}`}>
          {dup.settled !== "repeats" && <button type="button" class="btn small" disabled={busy} onClick={() => settle("repeats")}>Yes, it repeats it</button>}
          {dup.settled !== "different" && <button type="button" class="btn small" disabled={busy} onClick={() => settle("different")}>No, these are different</button>}
        </div>
      )}
    </>
  );
}

/**
 * The other side of a resemblance: what says it repeats this line. Somebody reading the original
 * learned nothing about it before, so the only way to find a pair was to happen upon the row that
 * repeats. Stated, not acted on: the judgement belongs to the line that carries the pointer, and it is
 * settled there.
 */
function RepeatedBy({ line, owner, mine }: { line: RecordLine; owner: string; mine: boolean }) {
  return (
    <>
      {(line.repeatedBy ?? []).map((r) => (
        <p key={r.id} class="item-dup">
          “{r.title}” {r.settled === "repeats" ? "is marked as repeating" : "might repeat"} this one.
          {" "}<Link href={itemHref(r.id, owner, mine)}>Compare them</Link>
        </p>
      ))}
    </>
  );
}

/**
 * The contract line that funds the work. Nothing is placed on a unit until its owner confirms
 * it, so a candidate has no line, and saying "No CLIN" is the honest answer rather than a blank.
 */
export function ClinTagView({ clin }: { clin: ClinTag | undefined }) {
  return clin
    ? <span class={`clin-tag ${clin.tone}`}>{clin.key}</span>
    : <span class="clin-tag none">No CLIN</span>;
}

/** Where the work sits, or why it sits nowhere yet. */
function whereLine(line: RecordLine): string {
  if (line.unitName) return line.unitName;
  if (line.status === "candidate") return "Nothing is placed on a unit until you confirm it";
  return line.programName ?? "On no unit";
}

/** "Open" beside the source when it can be opened: the email in Outlook, the meeting in Teams, the contract in CorpDash. */
function sourceOpen(line: RecordLine) {
  if (!line.source.url) return null;
  const label = line.source.kind === "mail" ? "Open email" : line.source.kind === "meeting" ? "Open meeting" : line.source.kind === "contract" ? "Open in CorpDash" : line.source.kind === "model" ? "Open program" : line.source.kind === "ticket" ? "Open ticket" : line.source.kind === "page" ? "Open page" : line.source.kind === "chat" ? "Open chat" : "Open";
  return <> · <a class="src-link" href={line.source.url} target="_blank" rel="noopener">{label}</a></>;
}

/** The source line with the program appended; a summary must hold it whole or the program wraps alone. */
/**
 * Why a candidate sits where it does. Every phrase is read off the data behind the line, so
 * "Customer copied" appears only when a customer really is on that thread. The order these lines
 * came in is the whole of it: there is no number here, and nothing on this page scores a person.
 */
export function AttentionWhy({ line }: { line: RecordLine }) {
  const reasons = line.attention?.reasons ?? [];
  if (!reasons.length) return null;
  return (
    <p class="item-why">
      <span class="sr-only">Why this rates attention: </span>
      {reasons.map((r) => <span key={r} class="why-tag">{r}</span>)}
    </p>
  );
}

export function sourceLine(line: RecordLine): string {
  return line.programName ? `${line.source.label} · ${line.programName}` : line.source.label;
}

function describeRoles(r: RecordView): string {
  const byProgram = new Map<string, string[]>();
  for (const x of r.roles) byProgram.set(x.programName ?? x.programId, [...(byProgram.get(x.programName ?? x.programId) ?? []), x.roleName]);
  return [...byProgram].map(([p, roles]) => `${roles.join(", ")} on ${p}`).join(". ") + ".";
}

function Lock() {
  return <svg class="lock-ico" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>;
}
